(function () {
  'use strict';
  if (!window.customElements || customElements.get('km-cart-recommendations')) return;

  const cartReads = new Map();
  const pendingAdds = new Set();
  const displayLimit = 15;
  const events = ['cart:updated', 'cart:rendered', 'ajaxProduct:added', 'drawerOpen.CartDrawer'];
  const createId = () => window.crypto?.randomUUID?.()
    || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

  function readCart(root, event) {
    if (Array.isArray(event?.detail?.cart?.items) && (!window.KMCartState || event.type !== 'cart:updated')) {
      return Promise.resolve(event.detail.cart);
    }
    const drawer = document.getElementById('CartDrawer');
    if (event?.type === 'cart:rendered' && (!event.detail?.location || event.detail.location === 'cart-drawer')
      && drawer?.classList.contains('is-empty')
      && drawer.querySelector('[data-products] .cart__items')?.dataset.count === '0') {
      return Promise.resolve({items: [], item_count: 0, total_price: 0});
    }
    if (window.KMCartState) return window.KMCartState.read(root + 'cart.js');
    const token = event || null;
    const current = cartReads.get(root);
    if (current && current.token === token) return current.promise;
    const record = {token};
    record.promise = fetch(root + 'cart.js', {credentials: 'same-origin', cache: 'no-store'})
      .then(response => {
        if (!response.ok) throw new Error('Unable to load your cart.');
        return response.json();
      }).finally(() => {
        if (cartReads.get(root) === record) cartReads.delete(root);
      });
    cartReads.set(root, record);
    return record.promise;
  }

  function isPaidItem(item) {
    return Number(item.final_line_price ?? item.line_price ?? 0) > 0
      && isRecommendationItem(item);
  }

  function isRecommendationItem(item) {
    const properties = item.properties || {};
    return String(properties._km_cart_gift) !== 'true'
      && String(properties._uc_is_gift || '').trim() !== '1' && !properties._uc_pick_section;
  }

  function selectRows(rows, excluded, excludedCollections) {
    const seen = new Set(excluded);
    const collections = new Set(excludedCollections);
    return rows.filter(row => {
      const id = row.dataset.kmRecommendationProduct;
      if (!id || row.dataset.available === 'false' || seen.has(id)) return false;
      if ((row.dataset.collectionIds || '').split(',').some(collectionId => collections.has(collectionId.trim()))) return false;
      seen.add(id);
      return true;
    }).slice(0, displayLimit);
  }

  window.addEventListener('submit', function (event) {
    const form = event.target;
    if (!form || form.id !== 'CartDrawerForm' || !pendingAdds.size) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    pendingAdds.forEach(operation => operation.instance.setStatus(operation.instance.failedBuild
      ? 'Cart update required. Please retry.' : ''));
  }, true);

  class KMCartRecommendations extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-km-recommendation-list]');
      this.status = this.querySelector('[data-km-recommendation-status]');
      this.error = this.querySelector('[data-km-recommendation-error]');
      this.loader = this.querySelector('[data-km-recommendation-loading]');
      if (!this.list) return;
      this.root = (this.dataset.root || '/').replace(/\/?$/, '/');
      this.empty = this.dataset.empty === 'true';
      this.drawer = this.closest('#CartDrawer');
      this.media = window.matchMedia('(min-width: 769px)');
      this.onSelection = event => {
        this.selectedProductId = event.detail.item ? String(event.detail.item.product_id) : null;
        this.refresh(event);
      };
      this.onMedia = () => this.refresh();
      if (!this.collectionRows) this.collectionRows = [...this.list.querySelectorAll('[data-km-recommendation-product]')].map(row => row.cloneNode(true));
      this.revision = (this.revision || 0) + 1;
      this.onUpdate = event => {
        const nativeCart = this.closest('form')?.kmCartForm;
        if (this.failedBuild && !this.adding && event.type === 'cart:rendered'
          && (!event.detail || !event.detail.location || event.detail.location === 'cart-drawer')
          && (!nativeCart || !nativeCart.failedCartOperations || !nativeCart.failedCartOperations.has('recommendation'))) {
          this.failedBuild = false;
          pendingAdds.delete(this.operation);
          this.operation = null;
        }
        this.refresh(event);
      };
      this.onClick = event => {
        const add = event.target.closest('[data-km-recommendation-add]');
        const retry = event.target.closest('[data-km-recommendation-retry]');
        if (add && this.contains(add)) {
          const selector = add.closest('[data-km-recommendation-product]')?.querySelector('[data-km-recommendation-variant]');
          this.add(selector?.value || add.dataset.kmRecommendationAdd, false, add);
        }
        else if (retry && this.contains(retry)) {
          if (this.failedBuild) this.add(null, true);
          else if (this.failedVariant) this.add(this.failedVariant, false, null, this.failedAttribution);
          else { this.cache = null; this.refresh(); }
        }
      };
      this.onChange = event => {
        if (event.target.matches('[data-km-recommendation-variant]')) this.fitVariants();
      };
      events.forEach(name => document.addEventListener(name, this.onUpdate));
      this.drawer?.addEventListener('km:cart-selection', this.onSelection);
      this.media.addEventListener('change', this.onMedia);
      window.addEventListener('pageshow', this.onUpdate);
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.resizeObserver = new ResizeObserver(() => this.fitVariants());
      this.resizeObserver.observe(this);
      this.fitVariants();
      if (document.fonts) document.fonts.ready.then(() => { if (this.isConnected) this.fitVariants(); });
      this.refresh();
    }

    disconnectedCallback() {
      this.revision++;
      if (this.controller) this.controller.abort();
      this.cache = null;
      if (this.failedBuild) {
        pendingAdds.delete(this.operation);
        this.operation = null;
        this.failedBuild = false;
      }
      events.forEach(name => document.removeEventListener(name, this.onUpdate));
      this.drawer?.removeEventListener('km:cart-selection', this.onSelection);
      this.media?.removeEventListener('change', this.onMedia);
      window.removeEventListener('pageshow', this.onUpdate);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    fitVariants() {
      const context = document.createElement('canvas').getContext('2d');
      if (!context) return;
      this.querySelectorAll('[data-km-recommendation-variant]').forEach(select => {
        const style = getComputedStyle(select);
        context.font = style.fontStyle + ' ' + style.fontWeight + ' ' + style.fontSize + ' ' + style.fontFamily;
        const text = select.options[select.selectedIndex]?.textContent || '';
        const width = Math.ceil(context.measureText(text).width) + parseFloat(style.paddingLeft)
          + parseFloat(style.paddingRight) + parseFloat(style.borderLeftWidth) + parseFloat(style.borderRightWidth) + 2;
        const available = select.parentElement.clientWidth;
        select.style.width = (available > 0 ? Math.min(width, available) : width) + 'px';
      });
    }

    setStatus(message) {
      if (!this.status) return;
      this.status.textContent = message || '';
      this.status.hidden = !message;
    }

    updateBusy() {
      const busy = Boolean(this.loading || this.adding);
      this.setAttribute('aria-busy', String(busy));
      if (this.loader) this.loader.hidden = !busy;
    }

    showError(error) {
      if (!this.error) return;
      const message = this.error.querySelector('[data-km-recommendation-error-message]') || this.error;
      message.textContent = error.message || 'Unable to load recommendations. Please try again.';
      this.error.hidden = false;
    }

    async fetchRows(path, signal) {
      const response = await fetch(this.root + path, {credentials: 'same-origin', signal});
      if (!response.ok) throw new Error('Unable to load recommendations. Please try again.');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const container = html.querySelector('[data-km-recommendation-response]');
      if (!container) throw new Error('Unable to load recommendations. Please try again.');
      return {seed: container.dataset.seedId, rows: [...container.querySelectorAll('[data-km-recommendation-product]')]};
    }

    async loadRows(seeds, excluded, excludedCollections, signal) {
      let bestSellers;
      const fallback = async () => {
        if (!bestSellers) bestSellers = await this.fetchRows('collections/all?sort_by=best-selling&section_id=cart-drawer-recommendations', signal);
        return bestSellers;
      };
      if (this.empty && !seeds.length) {
        const best = await fallback();
        if (best.seed) seeds = [best.seed];
      }
      let rows = [];
      for (const seed of seeds.slice(0, 3)) {
        try {
          const native = await this.fetchRows('recommendations/products?section_id=cart-drawer-recommendations&product_id=' + encodeURIComponent(seed) + '&intent=related&limit=10', signal);
          rows = selectRows(rows.concat(native.rows), excluded, excludedCollections);
          if (rows.length >= displayLimit) break;
        } catch (error) {
          if (signal.aborted) throw error;
        }
      }
      if (rows.length < displayLimit) {
        try {
          rows = selectRows(rows.concat((await fallback()).rows), excluded, excludedCollections);
        } catch (error) {
          if (signal.aborted || !rows.length) throw error;
        }
      }
      return rows;
    }

    async refresh(event) {
      if (!this.isConnected || !this.list) return;
      const revision = ++this.revision;
      this.loading = true;
      this.updateBusy();
      this.setStatus('');
      if (this.error && !this.failedVariant && !this.failedBuild) this.error.hidden = true;
      try {
        const cart = await readCart(this.root, event);
        if (revision !== this.revision || !this.isConnected) return;
        const items = Array.isArray(cart.items) ? cart.items : [];
        this.hidden = this.empty !== (items.length === 0);
        if (this.hidden) {
          if (this.controller) this.controller.abort();
          this.cache = null;
          return;
        }
        const excluded = [...new Set(items.map(item => String(item.product_id)))].sort();
        const excludedCollections = (this.dataset.excludedCollectionIds || '').split(',').map(id => id.trim()).filter(Boolean).sort();
        let seeds = this.empty ? [this.dataset.productId].filter(Boolean)
          : [...new Set(items.filter(isPaidItem).map(item => String(item.product_id)))].slice(0, 3);
        if (!this.empty && this.media.matches) {
          const selected = items.find(item => String(item.product_id) === this.selectedProductId && isRecommendationItem(item))
            || items.find(isRecommendationItem);
          seeds = selected ? [String(selected.product_id)] : [];
        }
        const key = JSON.stringify([this.empty, seeds, excluded, excludedCollections]);
        let rows;
        if (this.empty && this.dataset.collectionOverride === 'true') rows = selectRows(this.collectionRows, excluded, excludedCollections);
        else {
          if (!this.cache || this.cache.key !== key) {
            if (this.controller) this.controller.abort();
            this.controller = new AbortController();
            this.cache = {key, promise: this.loadRows(seeds, excluded, excludedCollections, this.controller.signal)};
          }
          const current = this.cache;
          try { rows = await current.promise; }
          catch (error) { if (this.cache === current) this.cache = null; throw error; }
        }
        if (revision !== this.revision || !this.isConnected) return;
        const renderedRows = rows.map(row => row.cloneNode(true));
        const scene = this.empty ? 'empty_cart' : 'cart';
        const batchKey = JSON.stringify([scene, renderedRows.map(row => row.dataset.kmRecommendationProduct)]);
        if (this.batchKey !== batchKey) {
          this.batchKey = batchKey;
          this.batchId = createId();
        }
        renderedRows.forEach((row, index) => {
          row.dataset.kmRecommendationModule = 'ymal';
          row.dataset.kmRecommendationScene = scene;
          row.dataset.kmRecommendationBatchId = this.batchId;
          row.dataset.kmRecommendationPosition = String(index + 1);
        });
        this.list.replaceChildren(...renderedRows);
        this.fitVariants();
        if (this.adding) this.querySelectorAll('[data-km-recommendation-add]').forEach(button => { button.disabled = true; });
        this.setStatus(rows.length ? '' : 'No recommendations available right now.');
      } catch (error) {
        if (revision === this.revision && this.isConnected && error.name !== 'AbortError') {
          this.setStatus('');
          this.showError(error);
        }
      } finally {
        if (revision === this.revision) {
          this.loading = false;
          this.updateBusy();
          if (this.hidden) this.setStatus('');
        }
      }
    }

    async add(variantId, rebuildOnly, button, retryAttribution) {
      if (this.adding || (!rebuildOnly && (this.failedBuild || !/^\d+$/.test(variantId)))) return;
      const closestForm = this.closest('form');
      const form = closestForm && closestForm.isConnected ? closestForm : document.getElementById('CartDrawerForm');
      const nativeCart = form && form.kmCartForm;
      const checkoutOperation = nativeCart && nativeCart.beginOperation && nativeCart.beginOperation('recommendation');
      let succeeded = false;
      this.adding = true;
      this.updateBusy();
      this.failedVariant = null;
      this.failedAttribution = null;
      const operation = this.operation || {instance: this};
      this.operation = operation;
      pendingAdds.add(operation);
      if (this.error) this.error.hidden = true;
      this.querySelectorAll('[data-km-recommendation-add], [data-km-recommendation-retry]').forEach(button => { button.disabled = true; });
      if (!button && variantId) button = this.querySelector('[data-km-recommendation-add="' + variantId + '"]');
      const recommendationCard = button?.closest('[data-km-recommendation-product]');
      const analyticsProduct = window.KMCartAnalytics?.product(recommendationCard)
        || this.failedAnalyticsProduct;
      const source = this.closest('#CartPage, [data-km-cart-page]') ? 'page' : 'drawer';
      const attribution = rebuildOnly ? null : retryAttribution || Object.fromEntries(Object.entries({
        _recommendation_source: `${this.empty ? 'empty_cart' : 'cart'}_${source}`,
        _recommendation_scene: this.empty ? 'empty_cart' : 'cart',
        _recommendation_module: 'ymal',
        _recommendation_add_id: createId(),
        _recommendation_batch_id: recommendationCard?.dataset.kmRecommendationBatchId,
        _recommendation_position: recommendationCard?.dataset.kmRecommendationPosition,
        _recommendation_product_id: analyticsProduct?.product_id,
        _recommendation_product_title: analyticsProduct?.product_title,
        _recommendation_product_handle: analyticsProduct?.product_handle,
        _recommendation_price: analyticsProduct?.price
      }).filter(([, value]) => value !== undefined && value !== null && value !== ''));
      if (button) button.setAttribute('aria-busy', 'true');
      this.setStatus('');
      let added = Boolean(rebuildOnly);
      try {
        if (!rebuildOnly) {
          const response = await (window.KMCartState?.fetch || window.fetch.bind(window))(this.root + 'cart/add.js', {
            method: 'POST', credentials: 'same-origin',
            headers: {'Content-Type': 'application/json', Accept: 'application/json'},
            body: JSON.stringify({items: [{id: Number(variantId), quantity: 1, properties: attribution}]})
          });
          const result = await response.json();
          if (!response.ok || result.status) throw new Error(result.description || result.message || 'Unable to add this item. Please try again.');
          added = true;
          window.KMCartAnalytics?.recommendationAdded(result, variantId, analyticsProduct);
          this.failedAnalyticsProduct = null;
        }
        document.dispatchEvent(new CustomEvent('cart:build'));
        if (nativeCart) await nativeCart.cartBuildPromise;
        this.failedBuild = false;
        succeeded = true;
        await this.refresh();
      } catch (error) {
        if (added) this.failedBuild = true;
        else {
          this.failedVariant = variantId;
          this.failedAttribution = attribution;
          this.failedAnalyticsProduct = analyticsProduct;
        }
        this.setStatus('');
        this.showError(added ? new Error('Item added, but the cart could not update. ' + error.message) : error);
      } finally {
        if (checkoutOperation) nativeCart.finishOperation(checkoutOperation, succeeded);
        this.adding = false;
        this.updateBusy();
        if (button) button.setAttribute('aria-busy', 'false');
        if (!this.failedBuild || !this.isConnected) {
          pendingAdds.delete(operation);
          this.operation = null;
        }
        this.querySelectorAll('[data-km-recommendation-add]').forEach(button => { button.disabled = Boolean(this.failedBuild); });
        this.querySelectorAll('[data-km-recommendation-retry]').forEach(button => { button.disabled = false; });
      }
    }
  }

  customElements.define('km-cart-recommendations', KMCartRecommendations);
})();
